import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {isEqual} from 'lodash';
import {MarkdownService} from '../../helpers/markdown.service';
import {MARKED_OPTIONS, provideMarkdown} from 'ngx-markdown';
import {marked} from 'marked';
import {undoRedoJustHappened} from '../../helpers/signal.helpers';
import {AutoResizeInputDirective} from '../../directives/auto-resize-input.directive';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  standalone: true,
  selector: 'maporium-title-description',
  templateUrl: './title-description-input.component.html',
  styleUrls: ['./title-description-input.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    AutoResizeInputDirective,
    TranslatePipe,
  ],
  providers: [
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true,
          breaks: true
        }
      }
    })
  ]
})
export class TitleDescriptionInputComponent implements OnChanges, OnInit, OnDestroy {
  @Input() fields: {
    title: string;
    description?: string;
  } | undefined;
  @Input() entityId: string | undefined;
  @Input() isEditing = false;
  @Input() isNew = false;
  @Input() isMap = false;
  @Input() noDescription = false;
  @Input() allowMultiLine = true;
  @Output() fieldsChange = new EventEmitter<{ title: string, description?: string, valid?: boolean }>();
  @Output() titleInputChange = new EventEmitter<string>();
  @Output() nameFieldFocus = new EventEmitter<void>();
  @Output() nameFieldBlur = new EventEmitter<void>();
  @Output() descriptionFieldFocus = new EventEmitter<void>();
  @Output() descriptionFieldBlur = new EventEmitter<void>();
  @ViewChild('titleInput') titleInput!: ElementRef;
  @ViewChild('textAreaElement') descriptionInput!: ElementRef;

  isMarkdownEnabled = false;

  formGroup = new FormGroup({
    title: new FormControl('', Validators.required),
    description: new FormControl('')
  });
  document = document;
  isUpdatingFromServer = false;
  private updateTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private markdownService: MarkdownService) {
    effect(() => {
      this.isMarkdownEnabled = this.markdownService.markdownEnabledSignal;
    });

    // Signal → form sync is handled by @Input [fields] → ngOnChanges.
    // No direct signal subscription needed here — it caused redundant patches
    // and CD scheduling conflicts that led to freezes in prod mode.
  }

  get description(): string {
    return MarkdownService.cleanUpMarkdown(marked(MarkdownService.convertToMarkdown(this.fields?.description || '')) as string);
  }

  ngOnInit() {
    if (this.fields && this.isEditing) {
      this.formGroup.patchValue(this.fields, {emitEvent: false});
    }
    this.formGroup.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr))
      )
      .subscribe((value) => {
        if (!this.isUpdatingFromServer && !undoRedoJustHappened()) {
          const fields = this.formGroup.getRawValue();
          this.fieldsChange.emit({...fields, valid: this.formGroup.valid} as {
            title: string, description?: string, valid?: boolean
          });
        }
      });
  }


  ngOnDestroy(): void {
    // Clean up any pending timeout
    if (this.updateTimeoutId) {
      clearTimeout(this.updateTimeoutId);
      this.updateTimeoutId = null;
    }
    // Skip emitting stale form values if undo/redo just happened
    // This prevents re-applying undone changes when the form is destroyed
    if (undoRedoJustHappened()) {
      return;
    }
    const fields = this.formGroup.getRawValue();
    this.fieldsChange.emit({...fields, valid: this.formGroup.valid} as { title: string, description?: string, valid?: boolean});
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isNew && changes['isNew']?.firstChange) {
      this.formGroup.patchValue(this.fields!, {emitEvent: false});
      this.selectNameInput();
      return;
    }
    const idsDoNotMatch = changes['entityId'] && changes['entityId'].currentValue !== changes['entityId'].previousValue;
    const fieldsChanged = changes['fields'] && !isEqual(changes['fields'].currentValue, changes['fields'].previousValue);
    const forceUndoRedoSync = undoRedoJustHappened();

    if (this.fields) {
      const incomingFields = this.fields;
      const currentFormValues = this.formGroup.value;
      const mergedValues = {...currentFormValues};
      // MAP-1543: never overwrite a control the user is actively typing in (its
      // textarea has focus). Previously only the title was protected, so a stale
      // re-emit of the same element mid-edit (e.g. before a debounced save synced
      // it back) clobbered the description — pasted text would flash then vanish,
      // and on links the dropped text was then persisted. Guard both fields.
      const titleHasFocus = this.titleInput?.nativeElement === document.activeElement;
      const descriptionHasFocus = this.descriptionInput?.nativeElement === document.activeElement;
      const skipTitle = titleHasFocus && !idsDoNotMatch && !forceUndoRedoSync;
      const skipDescription = descriptionHasFocus && !idsDoNotMatch && !forceUndoRedoSync;
      Object.keys(incomingFields).forEach((key) => {
        if (key === 'title' && skipTitle) return;
        if (key === 'description' && skipDescription) return;
        const fieldControl = this.formGroup.get(key);
        const incomingValueDifferent = (incomingFields as any)[key] !== (currentFormValues as any)[key];
        if (fieldControl?.pristine || idsDoNotMatch || forceUndoRedoSync || (fieldsChanged && incomingValueDifferent)) {
          (mergedValues as any)[key] = (incomingFields as any)[key];
        }
      });

      if (!isEqual(mergedValues, currentFormValues)) {
        this.formGroup.patchValue(mergedValues, {emitEvent: false});
      }
      if (forceUndoRedoSync || idsDoNotMatch) {
        this.formGroup.markAsPristine();
      } else if (fieldsChanged) {
        // Mark each control pristine only if it wasn't skipped (i.e. not being
        // actively edited); a skipped, focused field stays dirty so its edit is
        // preserved and later saved.
        if (!skipTitle) this.formGroup.get('title')?.markAsPristine();
        if (!skipDescription) this.formGroup.get('description')?.markAsPristine();
      }
    }
  }



  selectNameInput() {
    setTimeout(() => {
      this.titleInput?.nativeElement?.select();
      this.isNew = false;
    }, 200);
  }

  onEnter($event: any) {
    if(!this.allowMultiLine) {
      $event.preventDefault();
    }
  }

  onTitleInput(event: Event) {
    this.titleInputChange.emit((event.target as HTMLTextAreaElement).value);
  }

  onTitleFocus() {
    this.nameFieldFocus.emit();
  }

  onTitleBlur() {
    this.nameFieldBlur.emit();
  }

  onDescriptionFocus() {
    this.descriptionFieldFocus.emit();
  }

  onDescriptionBlur() {
    this.descriptionFieldBlur.emit();
  }
}
